import { isValid, parse } from 'date-fns';

import { ANSWER_TYPE } from 'config/questionOptions';
import {
  castDateTimeToSimpleDate,
  formatDateToHTMLDateString,
} from './dateHelpers';

// Mirrors the answer formats accepted by the backend's date-measure util.
const DATE_ANSWER_FORMATS = ['yyyy-MM-dd', 'MM/dd/yyyy', 'yyyyMMdd'];

/**
 * Parses a date-measure answer string in any of the accepted formats.
 *
 * @param {String} value Raw answer string
 * @returns {Date|null} Parsed date, or null when the value is not a valid date
 */
export function parseDateAnswer(value) {
  if (typeof value !== 'string' || !value.trim().length) {
    return null;
  }

  // ISO date-times are reduced to their calendar date first — parsing them
  // in local time could shift the date across the day boundary. The iPad
  // reports averaged answers as decimal strings (e.g. "20260902.000").
  const raw = castDateTimeToSimpleDate(value.trim()).replace(
    /^(\d{8})\.0+$/,
    '$1',
  );
  const referenceDate = new Date();
  for (const dateFormat of DATE_ANSWER_FORMATS) {
    const parsed = parse(raw, dateFormat, referenceDate);
    if (isValid(parsed)) {
      return parsed;
    }
  }

  return null;
}

/**
 * Normalizes a date-measure answer string to the ISO YYYY-MM-DD format.
 *
 * @param {String} value Raw answer string
 * @returns {String|null} ISO date string, or null when the value is not a valid date
 */
export function normalizeDateAnswer(value) {
  const parsed = parseDateAnswer(value);
  return parsed ? formatDateToHTMLDateString(parsed) : null;
}

/**
 * Whether a question is a quantitative question measured as a Date.
 *
 * @param {Object} question
 * @returns {Boolean}
 */
export function isDateMeasureQuestion(question) {
  return (
    question?.answerType === ANSWER_TYPE.QUANTITATIVE_INPUT &&
    question?.expectedMeasureTableResult === 'Date'
  );
}
